import { ApiClient, curApiClient, HttpResponse } from './ApiClient';
import { BssPageListReply, BaseStation, AlertsPageListReply, FullAlert, Enterprise, EnrolProfile } from '@/types';

import _ from 'lodash';

export class ApiService {

  constructor(private apiClient:ApiClient) {

  }

  public async getBaseStations(query:{searchTerm?:string, includeTags?: string[], excludeTags?: string[]}):Promise<HttpResponse & BssPageListReply> {

    const reply = await this.apiClient.getBaseStations();

    if(reply.errorMsg)
      return Promise.resolve(reply);

    const allTagsSet = new Set<string>();
    reply.baseStationList!.forEach(bss => {
      (bss.tags || []).forEach(tag => allTagsSet.add(tag));
    });
    const allTags = Array.from(allTagsSet).sort();

    // we filter
    let bssList = reply.baseStationList!;
    
    if(query.searchTerm)
      bssList = this.filterBaseStations(bssList, query.searchTerm);

    // tag filtering
    if (query.includeTags || query.excludeTags)
      bssList = this.filterByTags(bssList, query.includeTags || [], query.excludeTags || []);

    // we sort the list
    this.sort(bssList, 'lrrRef', 'asc');

    // we split by pages and return
    const chunkedList = _.chunk(bssList, 50);

    const pageList = chunkedList.map((chunkedListItem: BaseStation[], index:number) => ({
      pageNum: index,
      bssList: chunkedListItem,
    }));

    // we return
    return Promise.resolve({pageList,allTags});
  }

  private filterBaseStations(bssList: BaseStation[], searchTerm: string):BaseStation[] {

    searchTerm = searchTerm.toLowerCase();

    return bssList.filter((bss) => {

      if(bss.name !== undefined && bss.name.toLowerCase().includes(searchTerm))
        return true;

      if(bss.lrrId !== undefined && bss.lrrId.toLowerCase().includes(searchTerm))
        return true;

      if(bss.lrrUuid !== undefined && bss.lrrUuid.toLowerCase().includes(searchTerm))
        return true;

      if(bss.supplierId !== undefined && bss.supplierId.toLowerCase().includes(searchTerm))
        return true;

      if(bss.commercialName !== undefined && bss.commercialName.toLowerCase().includes(searchTerm))
        return true;

      return false;
    });
  }

  private filterByTags(bssList: BaseStation[], includeTags: string[], excludeTags: string[]):BaseStation[] {

    // if no tags, we return the list as is
    if(includeTags.length === 0 && excludeTags.length === 0)
      return bssList;

    return bssList.filter((bss) => {
      const tags = bss.tags || [];

      // if we have include tags, we check if at least one matches
      if(includeTags.length > 0 && !includeTags.some(tag => tags.includes(tag)))
        return false;

      // if we have exclude tags, we check if none matches
      if(excludeTags.length > 0 && excludeTags.some(tag => tags.includes(tag)))
        return false;

      return true;
    });
  }

  public async getActiveAlerts(query:{searchTerm?:string, includeTags?: string[], excludeTags?: string[]}):Promise<HttpResponse & AlertsPageListReply> {

    // there should be only a few ks of active alerts
    let skip = 0;
    let alertList:FullAlert[] = [];

    while(true) {  
      const reply = await this.apiClient.getAlerts({activeOnly:true, limit:1000, skip:skip});

      if(reply.errorMsg)
        return Promise.resolve(reply);

      if(reply.alertList!.length === 0)
        break;

      skip+=1000;
      alertList.push(...reply.alertList!);
    }

    const allTagsSet = new Set<string>();
    alertList.forEach(alert => {
      const tags = (alert.baseStation && alert.baseStation.tags) ? alert.baseStation.tags : [];
      tags.forEach(tag => allTagsSet.add(tag));
    });
    const allTags = Array.from(allTagsSet).sort();

    // we filter
    if(query.searchTerm)
      alertList = this.filterAlerts(alertList, query.searchTerm);

    // Tag filtering
    if (query.includeTags || query.excludeTags) {
      alertList = this.filterAlertsByTags(alertList, query.includeTags || [], query.excludeTags || []);
    }

    // we sort the list
    this.sort(alertList, 'startTs', 'desc');

    // we split by pages and return
    const chunkedList = _.chunk(alertList, 50);
    const pageList = chunkedList.map((chunkedListItem: FullAlert[], index:number) => ({
      pageNum: index,
      alertList: chunkedListItem,
    }));

    // we return
    return Promise.resolve({pageList,allTags});
  }

  private filterAlerts(alertList: FullAlert[], searchTerm: string):FullAlert[] {

    searchTerm = searchTerm.toLowerCase();

    return alertList.filter((alert) => {

      if(alert.baseStation.name !== undefined && alert.baseStation.name.toLowerCase().includes(searchTerm))
        return true;

      if(alert.baseStation.lrrId !== undefined && alert.baseStation.lrrId.toLowerCase().includes(searchTerm))
        return true;

      if(alert.baseStation.lrrUuid !== undefined && alert.baseStation.lrrUuid.toLowerCase().includes(searchTerm))
        return true;

      if(alert.baseStation.supplierId !== undefined && alert.baseStation.supplierId.toLowerCase().includes(searchTerm))
        return true;

      if(alert.baseStation.commercialName !== undefined && alert.baseStation.commercialName.toLowerCase().includes(searchTerm))
        return true;

      return false;
    });
  }

  private filterAlertsByTags(alertList: FullAlert[], includeTags: string[], excludeTags: string[]): FullAlert[] {

  // if no tags, we return the list as is
  if (includeTags.length === 0 && excludeTags.length === 0)
    return alertList;

  return alertList.filter((alert) => {
    const tags = (alert.baseStation && alert.baseStation.tags) ? alert.baseStation.tags : [];

    // if we have include tags, we check if at least one matches
    if (includeTags.length > 0 && !includeTags.some(tag => tags.includes(tag)))
      return false;

    // if we have exclude tags, we check if none matches
    if (excludeTags.length > 0 && excludeTags.some(tag => tags.includes(tag)))
      return false;

    return true;
  });
}


  private sort(list: unknown[], key: string, ascDesc: 'asc'|'desc') {

    // we sort asc first
    list.sort((a: any, b: any) => {

      if (a[key] < b[key] || a[key] === undefined) {
        return ascDesc === 'asc' ? -1 : 1;
      } else if (a[key] > b[key] || b[key] === undefined) {
        return ascDesc === 'asc' ? +1 : -1;
      } else
        return 0;
    });
  }

  public async getEnterprisesPaginated(query:{searchTerm?:string}):Promise<HttpResponse & {pageList?:EntPage[]}> {

    const reply = await this.getEnterprises(query);

    if(reply.errorMsg)
      return Promise.resolve(reply);

    // we split by pages and return
    const chunkedList = _.chunk(reply.entList!, 50);
    const pageList = chunkedList.map((chunkedListItem: Enterprise[], index:number) => ({
      pageNum: index,
      entList: chunkedListItem,
    }));

    // we return
    return Promise.resolve({pageList});
  }

  public async getEnterprises(query:{searchTerm?:string}):Promise<HttpResponse & {entList?:Enterprise[]}> {

    // there should be only a few ks of active alerts
    let skip = 0;
    let entList:Enterprise[] = [];

    while(true) {  
      const reply = await this.apiClient.getEnterprises({limit:1000, skip:skip});

      if(reply.errorMsg)
        return Promise.resolve(reply);

      skip+=1000;
      entList.push(...reply.entList!);

      if(reply.entList!.length === 0 || reply.entList!.length < 1000)
        break;
    }
    
    // we filter
    if(query.searchTerm)
      entList = this.filterEnterprises(entList, query.searchTerm);

    // we sort the list
    this.sort(entList, 'enterpriseId', 'asc');

    return Promise.resolve({entList});
  }

  private filterEnterprises(entList: Enterprise[], searchTerm: string):Enterprise[] {

    searchTerm = searchTerm.toLowerCase();

    return entList.filter((ent) => {

      if(ent.enterpriseId.toLowerCase().includes(searchTerm))
        return true;

      if(ent.nwServer && ent.nwServer.actility && ent.nwServer.actility.enrolId && ent.nwServer.actility.enrolId.toLowerCase().includes(searchTerm))
        return true;

      if(ent.nwServer && ent.nwServer.actility && ent.nwServer.actility.subscriberId && `${ent.nwServer.actility.subscriberId}`.includes(searchTerm))
        return true;

      if(ent.nwServer && ent.nwServer.actility && ent.nwServer.actility.supplierId && `${ent.nwServer.actility.supplierId}`.includes(searchTerm))
        return true;

      if(ent.groupList && ent.groupList.length > 0 && JSON.stringify(ent.groupList).toLowerCase().includes(searchTerm))
        return true;

      return false;
    });

  }

  public async getEnrolProfilesPaginated(query:{searchTerm?:string}):Promise<HttpResponse & {pageList?:EnrolProfilePage[]}> {

    const reply = await this.getEnrolProfiles(query);

    if(reply.errorMsg)
      return Promise.resolve(reply);

    // we split by pages and return
    const chunkedList = _.chunk(reply.enrolProfileList!, 50);
    const pageList = chunkedList.map((chunkedListItem: EnrolProfile[], index:number) => ({
      pageNum: index,
      enrolProfileList: chunkedListItem,
    }));

    // we return
    return Promise.resolve({pageList});
  }

  public async getEnrolProfiles(query:{searchTerm?:string}):Promise<HttpResponse & {enrolProfileList?:EnrolProfile[]}> {

    // there should be only a few ks of active alerts
    let skip = 0;
    let enrolProfileList:EnrolProfile[] = [];

    while(true) {  
      const reply = await this.apiClient.getEnrolProfiles({limit:1000, skip:skip});

      if(reply.errorMsg)
        return Promise.resolve(reply);

      skip+=1000;
      enrolProfileList.push(...reply.enrolProfileList!);

      if(reply.enrolProfileList!.length === 0 || reply.enrolProfileList!.length < 1000)
        break;
    }
    
    // we sort the list
    this.sort(enrolProfileList, 'enrolId', 'asc');

    // we filter
    if(query.searchTerm)
      enrolProfileList = this.filterEnrolProfiles(enrolProfileList, query.searchTerm);

    return Promise.resolve({enrolProfileList});
  }

  private filterEnrolProfiles(enrolProfileList: EnrolProfile[], searchTerm: string):EnrolProfile[] {

    searchTerm = searchTerm.toLowerCase();

    return enrolProfileList.filter((enrolProf) => {

      if(enrolProf.enrolId.toLowerCase().includes(searchTerm))
        return true;

      if(enrolProf.description.toLowerCase().includes(searchTerm))
        return true; 
      
      if(enrolProf.type.toLowerCase().includes(searchTerm))
        return true; 

      if(enrolProf.actility && JSON.stringify(enrolProf.actility).toLowerCase().includes(searchTerm))
        return true;
      
      return false;
    });

  }

}

export interface EntPage {
  pageNum: number;
  entList: Enterprise[];
}

export interface EnrolProfilePage {
  pageNum: number;
  enrolProfileList: EnrolProfile[];
}

export const curApiService = new ApiService(curApiClient);
